import React from 'react'
import { connect } from 'react-redux'

import { setLibraryApproval, libraryApprovalCompleted } from '../../state/actions/app'
import { createRequestFor, markApproved, messageForFailure } from '../../classes/libraryPolicyApproval'

const DIALOG_ID = "approve-library-dialog"

// The severities a library policy can carry, value and label, exactly as the
// dashboard's own policy form offers them (see libraryPolicySeverityTemplate in
// sources/dashboard/js/mustache.templates/library.policies.js). The dashboard's
// leading "Select..." entry is deliberately absent: here the violation's own
// severity is always the starting point, so there is no empty choice to make.
const POLICY_SEVERITIES = [
    { value: "CRITICAL", label: "Critical" },
    { value: "HIGH", label: "High" },
    { value: "MEDIUM", label: "Medium" },
    { value: "LOW", label: "Low" },
    { value: "NONE", label: "None" },
    { value: "NA", label: "N/A" },
    { value: "SUGGEST", label: "Suggest" }
]

// Every violation observed carries one of the policy severities, so this only
// ever falls back on an unexpected value. It falls back to the loudest one
// rather than the quietest, so a policy created from a severity this form
// could not show still reports its violations instead of hiding them.
const severityFor = (advice) => {
    const isPolicySeverity = POLICY_SEVERITIES.some((severity) => severity.value === advice.severity)
    return isPolicySeverity ? advice.severity : POLICY_SEVERITIES[0].value
}

const mapStateAsProps = (state) => ({
    approvalState: state.libraryApprovalState,
    reportUuid: state.projectInfo.report ? state.projectInfo.report.uuid : null
})

const mapDispatchAsProps = (dispatch) => ({
    closeLibraryApproval: () => dispatch(setLibraryApproval(null)),
    completeLibraryApproval: (adviceId, version) => dispatch(libraryApprovalCompleted(adviceId, version))
})

// The create half of approving a library-policy violation: the update half
// never reaches here, because an existing policy only has its version range
// widened and is done without prompting (see ApproveLibrary.js).
class ApproveLibraryModal extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            ready: false,
            sending: false,
            description: "",
            versionRange: "",
            severity: "",
            errorMessage: null
        }
    }

    // A fresh pending approval must start from a fresh form: the fields belong
    // to one violation, and the modal instance is reused for the next one.
    getSnapshotBeforeUpdate(prevProps) {
        const previous = prevProps.approvalState
        const current = this.props.approvalState
        if (!current.advice) return false
        if (!previous.advice) return true
        return previous.advice.id !== current.advice.id || previous.version !== current.version
    }

    componentDidUpdate(_, __, isNewApproval) {
        if (!isNewApproval) return
        const { advice, version } = this.props.approvalState
        this.setState({
            ready: true,
            sending: false,
            description: "",
            versionRange: version,
            severity: severityFor(advice),
            errorMessage: null
        })
    }

    hideDialog() {
        window.$("#" + DIALOG_ID).modal("hide")
    }

    closeModal = () => {
        this.hideDialog()
        this.setState({ ready: false })
        this.props.closeLibraryApproval()
    }

    createPolicy = async () => {
        const { advice, version, teamUuid } = this.props.approvalState
        this.setState({ sending: true, errorMessage: null })
        try {
            // The library object is the one part of the request the user
            // cannot edit, so it keeps coming from the one place that decides
            // what a create request carries.
            const request = createRequestFor(advice, version)
            await window.meterian.teams.libraryPolicies.create(teamUuid, {
                library: request.library,
                versionRange: this.state.versionRange,
                severity: this.state.severity,
                description: this.state.description
            })
            markApproved(this.props.reportUuid, advice.id, version)
            this.hideDialog()
            this.setState({ ready: false, sending: false })
            this.props.completeLibraryApproval(advice.id, version)
        }
        catch (failure) {
            this.setState({ sending: false, errorMessage: messageForFailure(failure) })
        }
    }

    render() {
        const { advice } = this.props.approvalState
        if (!this.props.approvalState.ready || !this.state.ready || !advice) return null
        setTimeout(() => { window.$("#" + DIALOG_ID).modal("show") })
        const library = advice.library || { name: "", language: "" }
        return (
            <div className="modal fade user-modal" id={DIALOG_ID} tabIndex="-1" role="dialog">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h4 className="modal-title" id="approve-library-title">Approve library</h4>
                            <button type="button" className="close" aria-label="Close" onClick={this.closeModal}><span aria-hidden="true">&times;</span></button>
                        </div>
                        <div className="modal-body">
                            <form>
                                <div className="form-group">
                                    <label htmlFor="approve-library-name">Library:</label>
                                    <p className="form-control-static" id="approve-library-name" data-testid="approve-library-name">{library.name} ({library.language})</p>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="approve-library-description">Description:</label>
                                    <textarea className="form-control" id="approve-library-description" placeholder="Why this library version is approved"
                                        value={this.state.description}
                                        onChange={(event) => this.setState({ description: event.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="approve-library-version-range">Version range:</label>
                                    <input type="text" className="form-control" id="approve-library-version-range" placeholder="Approved version range"
                                        value={this.state.versionRange}
                                        onChange={(event) => this.setState({ versionRange: event.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="approve-library-severity">Severity:</label>
                                    <select className="form-control" id="approve-library-severity"
                                        value={this.state.severity}
                                        onChange={(event) => this.setState({ severity: event.target.value })}>
                                        {POLICY_SEVERITIES.map((severity) => (
                                            <option key={severity.value} value={severity.value}>{severity.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </form>
                            {this.state.errorMessage ?
                                <div className="alert alert-danger" role="alert">
                                    {this.state.errorMessage}
                                </div>
                                : null
                            }
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-default" onClick={this.closeModal}>Cancel</button>
                            <button type="button" className="btn btn-primary" id="approve-library-submit"
                                disabled={this.state.sending}
                                onClick={this.createPolicy}>
                                {this.state.sending ? "Creating…" : "Create policy"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

export default connect(mapStateAsProps, mapDispatchAsProps)(ApproveLibraryModal)
